import { ArrowRight, AlertTriangle, Zap, RefreshCw, XCircle } from "lucide-react";

interface Edge {
  source: string;
  target: string;
  type: string;
  weight: number;
}

interface GraphNode {
  id: string;
  label: string;
}

interface Props {
  edges: Edge[];
  nodes: GraphNode[];
}

interface Chain {
  path: { nodeId: string; label: string }[];
  edgeTypes: string[];
  totalWeight: number;
  category: "conflict" | "trigger" | "reinforce" | "suppress";
}

const CATEGORY_CONFIG = {
  conflict: { label: "Conflicts", icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50", border: "border-red-200" },
  trigger: { label: "Trigger Chains", icon: Zap, color: "text-orange-500", bg: "bg-orange-50", border: "border-orange-200" },
  reinforce: { label: "Reinforcement Loops", icon: RefreshCw, color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200" },
  suppress: { label: "Suppression Patterns", icon: XCircle, color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200" },
};

function isStructuralNode(id: string): boolean {
  return id.startsWith("phase_o") || id.startsWith("fv_") || id.startsWith("subdomain_") || id.startsWith("sub_");
}

function extractChains(edges: Edge[], nodeMap: Map<string, string>): Chain[] {
  const chains: Chain[] = [];
  const adjacency = new Map<string, { target: string; type: string; weight: number }[]>();

  // Only keep edges between evidence/phenomenon nodes with high-signal types
  const filtered = edges.filter(e =>
    !isStructuralNode(e.source) && !isStructuralNode(e.target) &&
    ["CONFLICT", "TRIGGER", "CAUSE", "REINFORCE", "SUPPRESS"].includes(e.type)
  );
  filtered.forEach((e) => {
    if (!adjacency.has(e.source)) adjacency.set(e.source, []);
    adjacency.get(e.source)!.push({ target: e.target, type: e.type, weight: e.weight });
  });

  const visited = new Set<string>();

  function dfs(nodeId: string, path: string[], edgeTypes: string[], totalWeight: number, primaryType: string) {
    const key = path.join("->");
    if (visited.has(key)) return;
    // Only record chains with 3+ nodes (A→B→C minimum)
    if (path.length >= 3) {
      const category = primaryType === "CONFLICT" ? "conflict"
        : primaryType === "SUPPRESS" ? "suppress"
        : primaryType === "REINFORCE" ? "reinforce"
        : "trigger";

      chains.push({
        path: path.map((id) => ({ nodeId: id, label: nodeMap.get(id) || id })),
        edgeTypes: [...edgeTypes],
        totalWeight,
        category,
      });
    }
    if (path.length >= 4) return;
    visited.add(key);

    const neighbors = adjacency.get(nodeId) || [];
    for (const nb of neighbors) {
      if (path.includes(nb.target)) {
        // Cycle detected — record as reinforcement loop if 3+ nodes
        if (path.length >= 2) {
          chains.push({
            path: [...path, nb.target].map((id) => ({ nodeId: id, label: nodeMap.get(id) || id })),
            edgeTypes: [...edgeTypes, nb.type],
            totalWeight: totalWeight + nb.weight,
            category: "reinforce",
          });
        }
        continue;
      }
      dfs(nb.target, [...path, nb.target], [...edgeTypes, nb.type], totalWeight + nb.weight, primaryType || nb.type);
    }
  }

  // Start from high-weight edges only
  const highSignalEdges = filtered
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 15);

  for (const edge of highSignalEdges) {
    dfs(edge.source, [edge.source, edge.target], [edge.type], edge.weight, edge.type);
  }

  // Dedupe by path signature, keep highest weight
  const seen = new Map<string, Chain>();
  for (const chain of chains) {
    const sig = chain.path.map((p) => p.nodeId).join("→");
    if (!seen.has(sig) || seen.get(sig)!.totalWeight < chain.totalWeight) {
      seen.set(sig, chain);
    }
  }

  // Filter by weight threshold, prioritize conflicts & reinforce loops, limit to 6
  return Array.from(seen.values())
    .filter(c => c.totalWeight >= 0.5)
    .sort((a, b) => {
      const priority: Record<string, number> = { conflict: 0, reinforce: 1, suppress: 2, trigger: 3 };
      const pDiff = (priority[a.category] ?? 4) - (priority[b.category] ?? 4);
      return pDiff !== 0 ? pDiff : b.totalWeight - a.totalWeight;
    })
    .slice(0, 6);
}

export default function CausalChainsPanel({ edges, nodes }: Props) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n.label]));
  const chains = extractChains(edges, nodeMap);

  if (chains.length === 0) return null;

  const grouped = chains.reduce<Record<string, Chain[]>>((acc, chain) => {
    if (!acc[chain.category]) acc[chain.category] = [];
    acc[chain.category].push(chain);
    return acc;
  }, {});

  return (
    <div className="space-y-2 md:space-y-3">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Zap className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Causal Chains
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Key causal pathways extracted from the relationship graph
      </p>

      <div className="space-y-3 md:space-y-4">
        {(["conflict", "trigger", "reinforce", "suppress"] as const).map((cat) => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          const cfg = CATEGORY_CONFIG[cat];
          const CatIcon = cfg.icon;

          return (
            <div key={cat}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <CatIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
                <span className={`text-xs md:text-sm font-semibold ${cfg.color}`}>{cfg.label}</span>
                <span className="text-[10px] text-muted-foreground">({items.length})</span>
              </div>
              <div className="space-y-1.5">
                {items.map((chain, ci) => (
                  <div
                    key={ci}
                    className={`rounded-lg border ${cfg.border} ${cfg.bg} p-2 md:p-3`}
                  >
                    <div className="flex flex-wrap items-center gap-1">
                      {chain.path.map((node, ni) => (
                        <span key={ni} className="flex items-center gap-1">
                          <span className="text-xs md:text-sm font-medium text-foreground">
                            {node.label}
                          </span>
                          {ni < chain.edgeTypes.length && (
                            <span className="flex items-center gap-0.5 text-muted-foreground">
                              <ArrowRight className="h-3 w-3" />
                              <span className="text-[9px] md:text-[10px] font-mono uppercase">
                                {chain.edgeTypes[ni]}
                              </span>
                              <ArrowRight className="h-3 w-3" />
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
